import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useListContext } from "ra-core";
import { CircleX } from "lucide-react";

const FILTER_KEY = "task_types_pending@ov";

/** Parse "{send-quotation,follow-quotation}" → ["send-quotation", "follow-quotation"] */
function parseTaskTypes(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .replace(/[{}]/g, "")
    .split(",")
    .filter(Boolean);
}

/** Build ["send-quotation", "follow-quotation"] → "{send-quotation,follow-quotation}" */
function buildTaskTypesFilter(types: string[]): string {
  return `{${types.join(",")}}`;
}

function toggleTaskType(
  taskType: string,
  filterValues: Record<string, any>,
): Record<string, any> {
  const current = parseTaskTypes(filterValues[FILTER_KEY]);
  const isSelected = current.includes(taskType);
  const next = isSelected
    ? current.filter((t) => t !== taskType)
    : [...current, taskType];

  if (next.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [FILTER_KEY]: _removed, ...rest } = filterValues;
    return rest;
  }
  return { ...filterValues, [FILTER_KEY]: buildTaskTypesFilter(next) };
}

/**
 * A toggle button for a single task type in the filter sidebar.
 * Multiple task types share the same "task_types_pending@ov" filter key —
 * each click adds/removes its value from the combined set (true multi-select).
 */
export const TaskTypeToggleFilterButton = ({
  taskType,
  label,
  className,
  size = "sm",
}: {
  taskType: string;
  label: React.ReactElement | string;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon" | null | undefined;
}) => {
  const { filterValues, setFilters } = useListContext();
  const isSelected = parseTaskTypes(filterValues[FILTER_KEY]).includes(taskType);

  return (
    <Button
      variant={isSelected ? "secondary" : "ghost"}
      onClick={() => setFilters(toggleTaskType(taskType, filterValues))}
      className={cn(
        "cursor-pointer",
        "flex flex-row items-center justify-between gap-2 px-2.5 w-full",
        className,
      )}
      size={size}
    >
      {label}
      {isSelected && <CircleX className="opacity-50" />}
    </Button>
  );
};

/**
 * Shows only when the task type is active in the filter summary bar.
 * Clicking it removes just that type from the combined filter.
 */
export const ActiveTaskTypeFilterButton = ({
  taskType,
  label,
  className,
}: {
  taskType: string;
  label: React.ReactElement | string;
  className?: string;
}) => {
  const { filterValues, setFilters } = useListContext();
  const isSelected = parseTaskTypes(filterValues[FILTER_KEY]).includes(taskType);
  if (!isSelected) return null;

  return (
    <Button
      variant="secondary"
      onClick={() => setFilters(toggleTaskType(taskType, filterValues))}
      className={cn(
        "cursor-pointer",
        "flex flex-row items-center justify-between gap-2 px-2 w-full h-8",
        className,
      )}
      size="sm"
    >
      {label}
      <CircleX className="opacity-50" />
    </Button>
  );
};
